import { FC, useMemo } from 'react';
import { BankingPaymentRail, PaymentMethodType, Tooltip, withStyles } from 'vopay';
import styles from './ConvenienceFeeWithTooltip.module.scss';
import { ConvenienceFeeBreakdown } from 'UI';
import { useConvenienceFeesProvider } from '@/providers/ConvenienceFees.provider';
import { paymentMethodToElinxConnectionMethodMap } from '@/utils';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type ConvenienceFeeWithTooltipProps = {
  paymentMethodType: PaymentMethodType;
  country?: string;
  province?: string;
  bankingPaymentRail?: BankingPaymentRail | undefined;
};

const ConvenienceFeeWithTooltip: FC<ConvenienceFeeWithTooltipProps> = ({
  paymentMethodType,
  country,
  province,
  bankingPaymentRail,
}) => {
  const { getProcessingFee, showFees } = useConvenienceFeesProvider();
  const { t: tConvenience } = useTranslation('translations', { keyPrefix: 'ConvenienceFee' });

  const normalizedFees = useMemo(() => {
    return getProcessingFee({
      paymentMethod: paymentMethodToElinxConnectionMethodMap[paymentMethodType],
      province,
    });
  }, [paymentMethodType]);

  const regionType = useMemo(() => {
    const isCanada = country?.toLowerCase() === 'canada' || country?.toLowerCase() === 'ca';

    return isCanada
      ? tConvenience('province', { defaultValue: 'province' })
      : tConvenience('state', { defaultValue: 'state' });
  }, [country]);

  const isFree = useMemo(() => {
    if (normalizedFees === null) return true;

    return normalizedFees.processingFeeNumeric === 0 && normalizedFees.transactionFeeNumeric === 0;
  }, [normalizedFees]);

  return (
    <div className={cn('feeWithTooltip')}>
      <ConvenienceFeeBreakdown
        paymentMethodType={paymentMethodType}
        province={province}
        bankingPaymentRail={bankingPaymentRail}
      />
      {showFees && !isFree && (
        <Tooltip
          icon="info"
          variant="outlined"
          iconWidth="10px"
          buttonWidth="18px"
          position="right"
          information={tConvenience('feesVaryBy', {
            regionType,
            defaultValue: `Fees vary by ${regionType}. This payment method includes a fee based on your billing address.`,
          })}
          hasArrow
          width="340px"
        />
      )}
    </div>
  );
};

export default ConvenienceFeeWithTooltip;
